import React, { useState } from "react";
import {
    Box,
    Typography,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import { formatCurrencyBDT } from "../../../utils/currency";
import { POS_PAYMENT_METHODS } from "../../../utils/paymentMethods";
import { PaymentManagement } from "../../../components/orders/PaymentManagement";
import { KeyHint } from "../../../components/common/KeyHint";
import { SUBMIT_SHORTCUT_LABEL, isSubmitShortcut } from "../../../utils/keyboard";
import { POSPayment } from "../types";

interface PaymentDialogProps {
    open: boolean;
    onClose: () => void;
    total: number;
    subtotal: number;
    deliveryCharge: number;
    discount: number;
    onPayment: (payments: POSPayment[]) => void;
    loading?: boolean;
}

export const PaymentDialog: React.FC<PaymentDialogProps> = ({
    open,
    onClose,
    total,
    subtotal,
    deliveryCharge,
    discount,
    onPayment,
    loading = false,
}) => {
    const [payments, setPayments] = useState<POSPayment[]>([]);

    // Each sale starts with no payments: "Add Payment" opens by itself with
    // the full amount filled in, so the operator only picks the method and
    // the account it landed in (see PaymentManagement's keyboard flow).
    // Reset while rendering, not in an effect, so the payments panel never
    // sees the previous sale's payments when it opens.
    const [wasOpen, setWasOpen] = useState(open);
    if (open !== wasOpen) {
        setWasOpen(open);
        if (open) setPayments([]);
    }

    const amountPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    const change = amountPaid - total;
    // Nothing taken yet — completing now records the whole sale as due.
    const unpaid = total > 0 && amountPaid === 0;

    const handleSubmit = () => {
        if (loading) return;
        onPayment(payments);
    };

    // Ctrl/⌘+Enter completes the sale — caught on the way down (capture) so
    // no focused control acts on it too, and only for keys typed in this
    // dialog itself, not in "Add Payment" or another popup on top of it.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!isSubmitShortcut(e.nativeEvent) || !e.currentTarget.contains(e.target as Node)) return;
        e.preventDefault();
        e.stopPropagation();
        handleSubmit();
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSubmit();
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            onKeyDownCapture={handleKeyDown}
        >
            <DialogTitle>Process Payment</DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={handleFormSubmit} sx={{ width: "100%" }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Order Summary
                        </Typography>

                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                backgroundColor: "grey.50",
                                borderRadius: 1,
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="body2">Subtotal:</Typography>
                                <Typography variant="body2">
                                    {formatCurrencyBDT(subtotal)}
                                </Typography>
                            </Box>
                            {deliveryCharge > 0 && (
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                    <Typography variant="body2">Delivery Charge:</Typography>
                                    <Typography variant="body2">
                                        +{formatCurrencyBDT(deliveryCharge)}
                                    </Typography>
                                </Box>
                            )}
                            {discount > 0 && (
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                    <Typography variant="body2">Discount:</Typography>
                                    <Typography variant="body2">
                                        -{formatCurrencyBDT(discount)}
                                    </Typography>
                                </Box>
                            )}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    pt: 1,
                                    borderTop: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <Typography variant="h6">Total:</Typography>
                                <Typography variant="h6">{formatCurrencyBDT(total)}</Typography>
                            </Box>
                        </Box>
                    </Box>

                    <PaymentManagement
                        payments={payments}
                        totalAmount={total}
                        onChange={setPayments}
                        paymentMethods={POS_PAYMENT_METHODS}
                        disabled={loading}
                        autoOpenAddPayment
                        keyboardFlow
                    />

                    {change > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Change to return: {formatCurrencyBDT(change)}
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button type="button" onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    color={unpaid ? "warning" : "primary"}
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? "Processing…" : unpaid ? "Complete as Unpaid" : "Complete Sale"}
                    {!loading && <KeyHint>{SUBMIT_SHORTCUT_LABEL}</KeyHint>}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
